/**
 * toasts.ts —— 全局 Toast 队列（设计方案 5.5：顶部居中、停留 3s、同时最多 1 条、新的替换旧的）
 *
 * v2（FR-B / AC-B.3）：结算 / 摊牌浮层在场期间 toast **静默排队**。
 * 结算态 CTA/倒计时是唯一推进路径，任何 toast 的淡入/淡出过程态都不允许压到浮层；
 * 因此浮层在场时 `setToastHold(true)` 把新 toast 收进静默队列（不展示、不启 TTL 计时），
 * 浮层关闭后 `setToastHold(false)` 再出队展示最近一条（沿用「同时最多 1 条、新替旧」语义）。
 */

import { createStore, type Store } from './store';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface ToastItem {
  readonly id: number;
  readonly text: string;
  readonly kind: ToastKind;
}

let seq = 0;
export const toastStore: Store<readonly ToastItem[]> = createStore<readonly ToastItem[]>([]);

/** 结算/摊牌期静默标志：true 时新 toast 进入静默队列而非立即展示 */
let held = false;
/** 静默期缓冲的待展示 toast（连同各自 TTL），释放时按「最多 1 条、新替旧」出队最近一条 */
let pending: Array<{ item: ToastItem; ttl: number }> = [];

function present(item: ToastItem, ttl: number): void {
  // 同时最多 1 条：新的直接替换旧的
  toastStore.set([item]);
  if (typeof setTimeout !== 'undefined') {
    setTimeout(() => {
      toastStore.set((prev) => prev.filter((t) => t.id !== item.id));
    }, ttl);
  }
}

export function pushToast(text: string, kind: ToastKind = 'info', ttl = 3000): number {
  seq += 1;
  const id = seq;
  const item: ToastItem = { id, text, kind };
  if (held) {
    // 静默期：只入队、不展示、不启 TTL 计时（避免过程态压结算浮层，AC-B.3）
    pending.push({ item, ttl });
    return id;
  }
  present(item, ttl);
  return id;
}

/**
 * 设置结算/摊牌期静默标志。
 * - 进入静默（true）：后续 toast 只入队不展示。
 * - 解除静默（false）：出队展示**最近一条**（同时最多 1 条、新替旧），其余丢弃。
 */
export function setToastHold(next: boolean): void {
  if (held === next) return;
  held = next;
  if (!held && pending.length > 0) {
    const last = pending[pending.length - 1];
    pending = [];
    present(last.item, last.ttl);
  }
}

/** 测试/复位辅助：清空静默标志与待展示队列（不影响已在展示的 toast） */
export function resetToastHold(): void {
  held = false;
  pending = [];
}

export function dismissToast(id: number): void {
  toastStore.set((prev) => prev.filter((t) => t.id !== id));
}
